// The live call: one RTCPeerConnection to every other participant (a mesh;
// fine for the studio's ≤6 people). Uses WebRTC's "perfect negotiation"
// pattern so two peers that offer at the same time don't deadlock: the
// polite side yields, the impolite side ignores the colliding offer.
//
// This carries the call only. Recordings are made from the local stream on
// each machine, so the call's quality never limits the recording.

type Msg = { type: string } & Record<string, unknown>;

export interface MeshChannel {
  send(msg: Record<string, unknown>): void;
  on(type: string, fn: (msg: Msg) => void): () => void;
}

export interface MeshOptions {
  selfId: string;
  channel: MeshChannel;
  iceServers: RTCIceServer[];
  localStream: MediaStream;
  /** Force media through TURN (to test the relay). */
  relayOnly?: boolean;
  onRemoteStream: (peerId: string, stream: MediaStream | null) => void;
  onPeerState?: (peerId: string, state: RTCPeerConnectionState) => void;
  PeerConnection?: typeof RTCPeerConnection;
  /** How long a connection may take before it's restarted. */
  watchdogMs?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

/** A connection not up this long after a peer appears gets an ICE restart… */
export const WATCHDOG_MS = 15_000;
/** …this many times at most. */
export const MAX_RESTARTS = 3;

interface Peer {
  pc: RTCPeerConnection;
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
  watchdog: unknown;
}

export class PeerMesh {
  private peers = new Map<string, Peer>();
  private unsubscribers: Array<() => void> = [];
  private stream: MediaStream;

  constructor(private o: MeshOptions) {
    this.stream = o.localStream;
  }

  start(): void {
    const on = (type: string, fn: (m: Msg) => void) => this.unsubscribers.push(this.o.channel.on(type, fn));
    on('welcome', (m) => {
      for (const p of (m.participants as Array<{ id: string }> | undefined) ?? []) this.connect(p.id);
    });
    on('peer.joined', (m) => {
      const p = m.participant as { id: string } | undefined;
      if (p) this.connect(p.id);
    });
    on('peer.left', (m) => this.drop(String(m.participantId)));
    on('signal', (m) => {
      // A bad description or candidate shouldn't take the call down.
      this.handleSignal(m).catch((err: unknown) => console.warn('studio: signal failed', err));
    });
  }

  /** Ids of the peers we have a connection to. */
  get peerIds(): string[] {
    return [...this.peers.keys()];
  }

  private connect(peerId: string): Peer {
    const existing = this.peers.get(peerId);
    if (existing) return existing;
    const PC = this.o.PeerConnection ?? RTCPeerConnection;
    const pc = new PC({ iceServers: this.o.iceServers, iceTransportPolicy: this.o.relayOnly ? 'relay' : 'all' });
    // The lower id yields when offers collide; both sides agree on that.
    const peer: Peer = { pc, polite: this.o.selfId < peerId, makingOffer: false, ignoreOffer: false, watchdog: null };
    this.peers.set(peerId, peer);
    this.watch(peerId, peer, 0);

    for (const track of this.stream.getTracks()) pc.addTrack(track, this.stream);
    pc.onnegotiationneeded = async () => {
      try {
        peer.makingOffer = true;
        await pc.setLocalDescription();
        this.o.channel.send({ type: 'signal', to: peerId, description: pc.localDescription });
      } catch {
        // Superseded by a newer negotiation.
      } finally {
        peer.makingOffer = false;
      }
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) this.o.channel.send({ type: 'signal', to: peerId, candidate: e.candidate });
    };
    pc.ontrack = (e) => this.o.onRemoteStream(peerId, e.streams[0] ?? null);
    pc.onconnectionstatechange = () => {
      this.o.onPeerState?.(peerId, pc.connectionState);
      if (pc.connectionState === 'failed') pc.restartIce();
    };
    return peer;
  }

  /**
   * Now and then a connection never gets going (seen on prod: both sides
   * stuck at "new" after the offer/answer). Restarting ICE renegotiates it.
   */
  private watch(peerId: string, peer: Peer, restarts: number) {
    const set = this.o.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    peer.watchdog = set(() => {
      peer.watchdog = null;
      if (this.peers.get(peerId) !== peer || peer.pc.connectionState === 'connected' || restarts >= MAX_RESTARTS) return;
      peer.pc.restartIce();
      this.watch(peerId, peer, restarts + 1);
    }, this.o.watchdogMs ?? WATCHDOG_MS);
  }

  private async handleSignal(m: Msg): Promise<void> {
    const from = String(m.from);
    const peer = this.connect(from);
    const { pc } = peer;
    const description = m.description as RTCSessionDescriptionInit | undefined;
    if (description) {
      const collision = description.type === 'offer' && (peer.makingOffer || pc.signalingState !== 'stable');
      peer.ignoreOffer = !peer.polite && collision;
      if (peer.ignoreOffer) return;
      await pc.setRemoteDescription(description);
      if (description.type === 'offer') {
        await pc.setLocalDescription();
        this.o.channel.send({ type: 'signal', to: from, description: pc.localDescription });
      }
      return;
    }
    if (m.candidate) {
      try {
        await pc.addIceCandidate(m.candidate as RTCIceCandidateInit);
      } catch (err) {
        // Candidates for an offer we ignored are expected to fail.
        if (!peer.ignoreOffer) throw err;
      }
    }
  }

  private drop(peerId: string) {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    if (peer.watchdog !== null) (this.o.clearTimer ?? ((t) => clearTimeout(t as ReturnType<typeof setTimeout>)))(peer.watchdog);
    peer.pc.close();
    this.peers.delete(peerId);
    this.o.onRemoteStream(peerId, null);
  }

  /** Swap in a new local stream (device change) without renegotiating. */
  async replaceStream(stream: MediaStream): Promise<void> {
    this.stream = stream;
    for (const { pc } of this.peers.values()) {
      for (const sender of pc.getSenders()) {
        const kind = sender.track?.kind;
        const next = stream.getTracks().find((t) => t.kind === kind);
        if (next) await sender.replaceTrack(next);
      }
    }
  }

  close(): void {
    this.unsubscribers.splice(0).forEach((u) => u());
    for (const id of [...this.peers.keys()]) this.drop(id);
  }
}
